import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import type { RouteRecordRaw } from 'vue-router';
import governmentResourcesRoutes from './governmentResources';

// Lazy loading dos componentes
const LoginView = () => import('@/views/auth/LoginView.vue');
const RegisterView = () => import('@/views/auth/RegisterView.vue');
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue');
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue');

const DashboardView = () => import('@/views/dashboard/DashboardView.vue');

// Layouts
const AuthLayout = () => import('@/layouts/AuthLayout.vue');
const DefaultLayout = () => import('@/layouts/DefaultLayout.vue');

// Admin routes
const AdminDashboard = () => import('@/views/admin/AdminDashboard.vue');
const UserManagement = () => import('@/views/admin/UserManagement.vue');
const SystemSettings = () => import('@/views/admin/SystemSettings.vue');
const Reports = () => import('@/views/admin/Reports.vue');

// Academic routes
const StudentManagement = () => import('@/views/academic/StudentManagement.vue');
const StudentProfile = () => import('@/views/academic/StudentProfile.vue');
const ClassManagement = () => import('@/views/academic/ClassManagement.vue');
const SubjectManagement = () => import('@/views/academic/SubjectManagement.vue');
const GradeManagement = () => import('@/views/academic/GradeManagement.vue');
const AttendanceManagement = () => import('@/views/academic/AttendanceManagement.vue');
const ScheduleManagement = () => import('@/views/academic/ScheduleManagement.vue');

// Financial routes
const FinancialDashboard = () => import('@/views/financial/FinancialDashboard.vue');
const FeeManagement = () => import('@/views/financial/FeeManagement.vue');
const PaymentManagement = () => import('@/views/financial/PaymentManagement.vue');
const FinancialReports = () => import('@/views/financial/FinancialReports.vue');

// Communication routes
const Messages = () => import('@/views/communication/Messages.vue');
const Notifications = () => import('@/views/communication/Notifications.vue');
const Announcements = () => import('@/views/communication/Announcements.vue');

// Profile routes
const Profile = () => import('@/views/profile/Profile.vue');
const Settings = () => import('@/views/profile/Settings.vue');

// Error pages
const NotFound = () => import('@/views/errors/NotFound.vue');
const Unauthorized = () => import('@/views/errors/Unauthorized.vue');
const ServerError = () => import('@/views/errors/ServerError.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/auth',
    component: AuthLayout,
    meta: { requiresGuest: true },
    children: [
      {
        path: '',
        redirect: '/auth/login',
      },
      {
        path: 'login',
        name: 'Login',
        component: LoginView,
        meta: {
          title: 'Login',
          description: 'Faça login em sua conta',
        },
      },
      {
        path: 'register',
        name: 'Register',
        component: RegisterView,
        meta: {
          title: 'Cadastro',
          description: 'Crie sua conta',
        },
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: ForgotPasswordView,
        meta: {
          title: 'Esqueci a Senha',
          description: 'Recupere sua senha',
        },
      },
      {
        path: 'reset-password',
        name: 'ResetPassword',
        component: ResetPasswordView,
        meta: {
          title: 'Redefinir Senha',
          description: 'Defina uma nova senha',
        },
      },
    ],
  },
  {
    path: '/',
    component: DefaultLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: DashboardView,
        meta: {
          title: 'Dashboard',
          description: 'Visão geral do sistema',
          icon: 'home',
        },
      },
      // Admin routes
      {
        path: 'admin',
        meta: {
          requiresAuth: true,
          requiresRole: ['admin'],
          title: 'Administração',
        },
        children: [
          {
            path: '',
            redirect: '/admin/dashboard',
          },
          {
            path: 'dashboard',
            name: 'AdminDashboard',
            component: AdminDashboard,
            meta: {
              title: 'Dashboard Admin',
              description: 'Painel administrativo',
              icon: 'cog',
            },
          },
          {
            path: 'users',
            name: 'UserManagement',
            component: UserManagement,
            meta: {
              title: 'Gerenciar Usuários',
              description: 'Administrar usuários do sistema',
              icon: 'users',
            },
          },
          {
            path: 'settings',
            name: 'SystemSettings',
            component: SystemSettings,
            meta: {
              title: 'Configurações do Sistema',
              description: 'Configurações gerais',
              icon: 'adjustments',
            },
          },
          {
            path: 'reports',
            name: 'AdminReports',
            component: Reports,
            meta: {
              title: 'Relatórios',
              description: 'Relatórios administrativos',
              icon: 'chart-bar',
            },
          },
        ],
      },
      // Academic routes
      {
        path: 'academic',
        meta: {
          requiresAuth: true,
          requiresRole: ['admin', 'director', 'coordinator', 'teacher'],
          title: 'Acadêmico',
        },
        children: [
          {
            path: 'students',
            name: 'StudentManagement',
            component: StudentManagement,
            meta: {
              title: 'Gerenciar Alunos',
              description: 'Administrar alunos',
              icon: 'academic-cap',
            },
          },
          {
            path: 'students/:id',
            name: 'StudentProfile',
            component: StudentProfile,
            meta: {
              title: 'Perfil do Aluno',
              description: 'Detalhes do aluno',
              icon: 'user',
            },
          },
          {
            path: 'classes',
            name: 'ClassManagement',
            component: ClassManagement,
            meta: {
              title: 'Gerenciar Turmas',
              description: 'Administrar turmas',
              icon: 'user-group',
            },
          },
          {
            path: 'subjects',
            name: 'SubjectManagement',
            component: SubjectManagement,
            meta: {
              title: 'Gerenciar Disciplinas',
              description: 'Administrar disciplinas',
              icon: 'book-open',
            },
          },
          {
            path: 'grades',
            name: 'GradeManagement',
            component: GradeManagement,
            meta: {
              title: 'Gerenciar Notas',
              description: 'Administrar notas',
              icon: 'clipboard-list',
            },
          },
          {
            path: 'attendance',
            name: 'AttendanceManagement',
            component: AttendanceManagement,
            meta: {
              title: 'Gerenciar Frequência',
              description: 'Controle de presença',
              icon: 'check-circle',
            },
          },
          {
            path: 'schedule',
            name: 'ScheduleManagement',
            component: ScheduleManagement,
            meta: {
              title: 'Gerenciar Horários',
              description: 'Administrar horários',
              icon: 'calendar',
            },
          },
        ],
      },
      // Financial routes
      {
        path: 'financial',
        meta: {
          requiresAuth: true,
          requiresRole: ['admin', 'director', 'financial'],
          title: 'Financeiro',
        },
        children: [
          {
            path: '',
            redirect: '/financial/dashboard',
          },
          {
            path: 'dashboard',
            name: 'FinancialDashboard',
            component: FinancialDashboard,
            meta: {
              title: 'Dashboard Financeiro',
              description: 'Visão geral financeira',
              icon: 'currency-dollar',
            },
          },
          {
            path: 'fees',
            name: 'FeeManagement',
            component: FeeManagement,
            meta: {
              title: 'Gerenciar Taxas',
              description: 'Administrar taxas e mensalidades',
              icon: 'receipt-tax',
            },
          },
          {
            path: 'payments',
            name: 'PaymentManagement',
            component: PaymentManagement,
            meta: {
              title: 'Gerenciar Pagamentos',
              description: 'Controle de pagamentos',
              icon: 'credit-card',
            },
          },
          {
            path: 'reports',
            name: 'FinancialReports',
            component: FinancialReports,
            meta: {
              title: 'Relatórios Financeiros',
              description: 'Relatórios e análises',
              icon: 'chart-pie',
            },
          },
        ],
      },
      // Patrimony routes
      {
        path: 'patrimony',
        meta: {
          requiresAuth: true,
          requiresRole: ['admin', 'director', 'patrimony'],
          title: 'Patrimônio',
        },
        children: [
          {
            path: '',
            redirect: '/patrimony/dashboard',
          },
          {
            path: 'dashboard',
            name: 'PatrimonyDashboard',
            component: () => import('@/views/patrimony/PatrimonyDashboard.vue'),
            meta: {
              title: 'Dashboard Patrimônio',
              description: 'Visão geral do patrimônio',
              icon: 'building-office',
            },
          },
          {
            path: 'assets',
            name: 'AssetsList',
            component: () => import('@/views/patrimony/AssetsList.vue'),
            meta: {
              title: 'Bens Patrimoniais',
              description: 'Gerenciar bens patrimoniais',
              icon: 'cube',
            },
          },
          {
            path: 'assets/new',
            name: 'AssetForm',
            component: () => import('@/views/patrimony/AssetForm.vue'),
            meta: {
              title: 'Novo Bem',
              description: 'Cadastrar bem patrimonial',
              icon: 'plus',
            },
          },
          {
            path: 'assets/:id',
            name: 'AssetDetail',
            component: () => import('@/views/patrimony/AssetDetail.vue'),
            meta: {
              title: 'Detalhes do Bem',
              description: 'Visualizar bem patrimonial',
              icon: 'eye',
            },
          },
          {
            path: 'assets/:id/edit',
            name: 'AssetEdit',
            component: () => import('@/views/patrimony/AssetForm.vue'),
            meta: {
              title: 'Editar Bem',
              description: 'Editar bem patrimonial',
              icon: 'pencil',
            },
          },
          {
            path: 'locations',
            name: 'LocationsList',
            component: () => import('@/views/patrimony/LocationsList.vue'),
            meta: {
              title: 'Localizações',
              description: 'Gerenciar localizações',
              icon: 'map-pin',
            },
          },
          {
            path: 'reports',
            name: 'PatrimonyReports',
            component: () => import('@/views/patrimony/PatrimonyReports.vue'),
            meta: {
              title: 'Relatórios Patrimoniais',
              description: 'Relatórios e análises',
              icon: 'document-text',
            },
          },
        ],
      },
      // Communication routes
      {
        path: 'communication',
        meta: {
          requiresAuth: true,
          title: 'Comunicação',
        },
        children: [
          {
            path: 'messages',
            name: 'Messages',
            component: Messages,
            meta: {
              title: 'Mensagens',
              description: 'Sistema de mensagens',
              icon: 'chat',
            },
          },
          {
            path: 'notifications',
            name: 'Notifications',
            component: Notifications,
            meta: {
              title: 'Notificações',
              description: 'Central de notificações',
              icon: 'bell',
            },
          },
          {
            path: 'announcements',
            name: 'Announcements',
            component: Announcements,
            meta: {
              title: 'Comunicados',
              description: 'Comunicados oficiais',
              icon: 'speakerphone',
            },
          },
        ],
      },
      // School Meals routes
      {
        path: 'school-meals',
        meta: {
          requiresAuth: true,
          requiresRole: ['admin', 'director', 'coordinator', 'nutritionist'],
          title: 'Merenda Escolar',
        },
        children: [
          {
            path: '',
            redirect: '/school-meals/dashboard',
          },
          {
            path: 'dashboard',
            name: 'SchoolMealsDashboard',
            component: () => import('@/views/school-meals/SchoolMealsDashboard.vue'),
            meta: {
              title: 'Dashboard Merenda',
              description: 'Visão geral da merenda escolar',
              icon: 'chart-bar',
            },
          },
          // Suppliers
          {
            path: 'suppliers',
            name: 'SuppliersList',
            component: () => import('@/views/school-meals/suppliers/SuppliersList.vue'),
            meta: {
              title: 'Fornecedores',
              description: 'Gerenciar fornecedores',
              icon: 'truck',
            },
          },
          {
            path: 'suppliers/new',
            name: 'SupplierForm',
            component: () => import('@/views/school-meals/suppliers/SupplierForm.vue'),
            meta: {
              title: 'Novo Fornecedor',
              description: 'Cadastrar fornecedor',
              icon: 'plus',
            },
          },
          {
            path: 'suppliers/:id/edit',
            name: 'SupplierEdit',
            component: () => import('@/views/school-meals/suppliers/SupplierForm.vue'),
            meta: {
              title: 'Editar Fornecedor',
              description: 'Editar dados do fornecedor',
              icon: 'pencil',
            },
          },
          {
            path: 'suppliers/:id',
            name: 'SupplierDetail',
            component: () => import('@/views/school-meals/suppliers/SupplierDetail.vue'),
            meta: {
              title: 'Detalhes do Fornecedor',
              description: 'Visualizar fornecedor',
              icon: 'eye',
            },
          },
          // Ingredients
          {
            path: 'ingredients',
            name: 'IngredientsList',
            component: () => import('@/views/school-meals/ingredients/IngredientsList.vue'),
            meta: {
              title: 'Ingredientes',
              description: 'Gerenciar ingredientes',
              icon: 'beaker',
            },
          },
          {
            path: 'ingredients/new',
            name: 'IngredientForm',
            component: () => import('@/views/school-meals/ingredients/IngredientForm.vue'),
            meta: {
              title: 'Novo Ingrediente',
              description: 'Cadastrar ingrediente',
              icon: 'plus',
            },
          },
          {
            path: 'ingredients/:id/edit',
            name: 'IngredientEdit',
            component: () => import('@/views/school-meals/ingredients/IngredientForm.vue'),
            meta: {
              title: 'Editar Ingrediente',
              description: 'Editar dados do ingrediente',
              icon: 'pencil',
            },
          },
          {
            path: 'ingredients/:id',
            name: 'IngredientDetail',
            component: () => import('@/views/school-meals/ingredients/IngredientDetail.vue'),
            meta: {
              title: 'Detalhes do Ingrediente',
              description: 'Visualizar ingrediente',
              icon: 'eye',
            },
          },
          // Purchases
          {
            path: 'purchases',
            name: 'PurchasesList',
            component: () => import('@/views/school-meals/purchases/PurchasesList.vue'),
            meta: {
              title: 'Compras',
              description: 'Gerenciar compras',
              icon: 'shopping-cart',
            },
          },
          {
            path: 'purchases/new',
            name: 'PurchaseForm',
            component: () => import('@/views/school-meals/purchases/PurchaseForm.vue'),
            meta: {
              title: 'Nova Compra',
              description: 'Registrar compra',
              icon: 'plus',
            },
          },
          {
            path: 'purchases/:id/edit',
            name: 'PurchaseEdit',
            component: () => import('@/views/school-meals/purchases/PurchaseForm.vue'),
            meta: {
              title: 'Editar Compra',
              description: 'Editar dados da compra',
              icon: 'pencil',
            },
          },
          {
            path: 'purchases/:id',
            name: 'PurchaseDetail',
            component: () => import('@/views/school-meals/purchases/PurchaseDetail.vue'),
            meta: {
              title: 'Detalhes da Compra',
              description: 'Visualizar compra',
              icon: 'eye',
            },
          },
          // Recipes
          {
            path: 'recipes',
            name: 'RecipesList',
            component: () => import('@/views/school-meals/recipes/RecipesList.vue'),
            meta: {
              title: 'Receitas',
              description: 'Gerenciar receitas',
              icon: 'document-text',
            },
          },
          {
            path: 'recipes/new',
            name: 'RecipeForm',
            component: () => import('@/views/school-meals/recipes/RecipeForm.vue'),
            meta: {
              title: 'Nova Receita',
              description: 'Cadastrar receita',
              icon: 'plus',
            },
          },
          {
            path: 'recipes/:id/edit',
            name: 'RecipeEdit',
            component: () => import('@/views/school-meals/recipes/RecipeForm.vue'),
            meta: {
              title: 'Editar Receita',
              description: 'Editar dados da receita',
              icon: 'pencil',
            },
          },
          {
            path: 'recipes/:id',
            name: 'RecipeDetail',
            component: () => import('@/views/school-meals/recipes/RecipeDetail.vue'),
            meta: {
              title: 'Detalhes da Receita',
              description: 'Visualizar receita',
              icon: 'eye',
            },
          },
          // Meals
          {
            path: 'meals',
            name: 'MealsList',
            component: () => import('@/views/school-meals/meals/MealsList.vue'),
            meta: {
              title: 'Cardápios',
              description: 'Gerenciar cardápios',
              icon: 'calendar',
            },
          },
          {
            path: 'meals/new',
            name: 'MealForm',
            component: () => import('@/views/school-meals/meals/MealForm.vue'),
            meta: {
              title: 'Novo Cardápio',
              description: 'Criar cardápio',
              icon: 'plus',
            },
          },
          {
            path: 'meals/:id/edit',
            name: 'MealEdit',
            component: () => import('@/views/school-meals/meals/MealForm.vue'),
            meta: {
              title: 'Editar Cardápio',
              description: 'Editar cardápio',
              icon: 'pencil',
            },
          },
          {
            path: 'meals/:id',
            name: 'MealDetail',
            component: () => import('@/views/school-meals/meals/MealDetail.vue'),
            meta: {
              title: 'Detalhes do Cardápio',
              description: 'Visualizar cardápio',
              icon: 'eye',
            },
          },
        ],
      },
      // Profile routes
      {
        path: 'profile',
        name: 'Profile',
        component: Profile,
        meta: {
          title: 'Meu Perfil',
          description: 'Informações pessoais',
          icon: 'user-circle',
        },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: Settings,
        meta: {
          title: 'Configurações',
          description: 'Preferências pessoais',
          icon: 'cog',
        },
      },
    ],
  },
  // Government Resources routes
  ...governmentResourcesRoutes,
  // Error pages
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: Unauthorized,
    meta: {
      title: 'Acesso Negado',
      description: 'Você não tem permissão para acessar esta página',
    },
  },
  {
    path: '/server-error',
    name: 'ServerError',
    component: ServerError,
    meta: {
      title: 'Erro do Servidor',
      description: 'Ocorreu um erro interno do servidor',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Página Não Encontrada',
      description: 'A página que você está procurando não existe',
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    } else {
      return { top: 0 };
    }
  },
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();
  
  // Initialize auth store if not already done
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      next({
        name: 'Login',
        query: { redirect: to.fullPath },
      });
      return;
    }

    // Check role-based access
    if (to.matched.some(record => record.meta.requiresRole)) {
      const requiredRoles = to.meta.requiresRole as string[];
      if (!authStore.hasAnyRole(requiredRoles)) {
        next({ name: 'Unauthorized' });
        return;
      }
    }
  }

  // Check if route requires guest (not authenticated)
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (authStore.isAuthenticated) {
      next({ name: 'Dashboard' });
      return;
    }
  }

  next();
});

// Update document title
router.afterEach(to => {
  const title = to.meta.title as string;
  if (title) {
    document.title = `${title} - Sistema Escolar`;
  } else {
    document.title = 'Sistema Escolar';
  }
});

export default router;

// Helper function to get menu items based on user role
export function getMenuItems(userRole: string) {
  const menuItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: 'home',
      roles: ['admin', 'director', 'coordinator', 'teacher', 'student', 'parent'],
    },
    {
      name: 'Acadêmico',
      icon: 'academic-cap',
      roles: ['admin', 'director', 'coordinator', 'teacher'],
      children: [
        {
          name: 'Alunos',
          path: '/academic/students',
          icon: 'academic-cap',
          roles: ['admin', 'director', 'coordinator', 'teacher'],
        },
        {
          name: 'Turmas',
          path: '/academic/classes',
          icon: 'user-group',
          roles: ['admin', 'director', 'coordinator'],
        },
        {
          name: 'Disciplinas',
          path: '/academic/subjects',
          icon: 'book-open',
          roles: ['admin', 'director', 'coordinator'],
        },
        {
          name: 'Notas',
          path: '/academic/grades',
          icon: 'clipboard-list',
          roles: ['admin', 'director', 'coordinator', 'teacher'],
        },
        {
          name: 'Frequência',
          path: '/academic/attendance',
          icon: 'check-circle',
          roles: ['admin', 'director', 'coordinator', 'teacher'],
        },
        {
          name: 'Horários',
          path: '/academic/schedule',
          icon: 'calendar',
          roles: ['admin', 'director', 'coordinator'],
        },
      ],
    },
    {
      name: 'Financeiro',
      icon: 'currency-dollar',
      roles: ['admin', 'director', 'financial'],
      children: [
        {
          name: 'Dashboard',
          path: '/financial/dashboard',
          icon: 'currency-dollar',
          roles: ['admin', 'director', 'financial'],
        },
        {
          name: 'Taxas',
          path: '/financial/fees',
          icon: 'receipt-tax',
          roles: ['admin', 'director', 'financial'],
        },
        {
          name: 'Pagamentos',
          path: '/financial/payments',
          icon: 'credit-card',
          roles: ['admin', 'director', 'financial'],
        },
        {
          name: 'Relatórios',
          path: '/financial/reports',
          icon: 'chart-pie',
          roles: ['admin', 'director', 'financial'],
        },
      ],
    },
    {
      name: 'Merenda Escolar',
      icon: 'cake',
      roles: ['admin', 'director', 'nutritionist', 'kitchen'],
      children: [
        {
          name: 'Dashboard',
          path: '/school-meals/dashboard',
          icon: 'chart-pie',
          roles: ['admin', 'director', 'nutritionist', 'kitchen'],
        },
        {
          name: 'Fornecedores',
          path: '/school-meals/suppliers',
          icon: 'truck',
          roles: ['admin', 'director', 'nutritionist'],
        },
        {
          name: 'Ingredientes',
          path: '/school-meals/ingredients',
          icon: 'beaker',
          roles: ['admin', 'director', 'nutritionist', 'kitchen'],
        },
        {
          name: 'Compras',
          path: '/school-meals/purchases',
          icon: 'shopping-cart',
          roles: ['admin', 'director', 'nutritionist'],
        },
        {
          name: 'Receitas',
          path: '/school-meals/recipes',
          icon: 'document-text',
          roles: ['admin', 'director', 'nutritionist', 'kitchen'],
        },
        {
          name: 'Cardápios',
          path: '/school-meals/meals',
          icon: 'calendar',
          roles: ['admin', 'director', 'nutritionist', 'kitchen'],
        },
      ],
    },
    {
      name: 'Recursos Governo',
      icon: 'building-office',
      roles: ['admin', 'director', 'government'],
      children: [
        {
          name: 'Dashboard',
          path: '/government-resources/dashboard',
          icon: 'chart-pie',
          roles: ['admin', 'director', 'government'],
        },
        {
          name: 'Programas',
          path: '/government-resources/programs',
          icon: 'folder',
          roles: ['admin', 'director', 'government'],
        },
        {
          name: 'Solicitações',
          path: '/government-resources/requests',
          icon: 'document-plus',
          roles: ['admin', 'director', 'government'],
        },
        {
          name: 'Orçamento',
          path: '/government-resources/budget',
          icon: 'currency-dollar',
          roles: ['admin', 'director', 'government'],
        },
        {
          name: 'Relatórios',
          path: '/government-resources/reports',
          icon: 'document-text',
          roles: ['admin', 'director', 'government'],
        },
      ],
    },
    {
      name: 'Patrimônio',
      icon: 'building-office-2',
      roles: ['admin', 'director', 'patrimony'],
      children: [
        {
          name: 'Dashboard',
          path: '/patrimony/dashboard',
          icon: 'chart-bar',
          roles: ['admin', 'director', 'patrimony'],
        },
        {
          name: 'Bens Patrimoniais',
          path: '/patrimony/assets',
          icon: 'cube',
          roles: ['admin', 'director', 'patrimony'],
        },
        {
          name: 'Localizações',
          path: '/patrimony/locations',
          icon: 'map-pin',
          roles: ['admin', 'director', 'patrimony'],
        },
        {
          name: 'Relatórios',
          path: '/patrimony/reports',
          icon: 'document-text',
          roles: ['admin', 'director', 'patrimony'],
        },
      ],
    },
    {
      name: 'Comunicação',
      icon: 'chat',
      roles: ['admin', 'director', 'coordinator', 'teacher', 'student', 'parent'],
      children: [
        {
          name: 'Mensagens',
          path: '/communication/messages',
          icon: 'chat',
          roles: ['admin', 'director', 'coordinator', 'teacher', 'student', 'parent'],
        },
        {
          name: 'Notificações',
          path: '/communication/notifications',
          icon: 'bell',
          roles: ['admin', 'director', 'coordinator', 'teacher', 'student', 'parent'],
        },
        {
          name: 'Comunicados',
          path: '/communication/announcements',
          icon: 'speakerphone',
          roles: ['admin', 'director', 'coordinator', 'teacher'],
        },
      ],
    },
    {
      name: 'Administração',
      icon: 'cog',
      roles: ['admin'],
      children: [
        {
          name: 'Dashboard',
          path: '/admin/dashboard',
          icon: 'cog',
          roles: ['admin'],
        },
        {
          name: 'Usuários',
          path: '/admin/users',
          icon: 'users',
          roles: ['admin'],
        },
        {
          name: 'Configurações',
          path: '/admin/settings',
          icon: 'adjustments',
          roles: ['admin'],
        },
        {
          name: 'Relatórios',
          path: '/admin/reports',
          icon: 'chart-bar',
          roles: ['admin'],
        },
      ],
    },
  ];

  return menuItems.filter(item => {
    if (item.roles.includes(userRole)) {
      if (item.children) {
        item.children = item.children.filter(child => child.roles.includes(userRole));
      }
      return true;
    }
    return false;
  });
}